/**
 * Vendor Bot Shop - Payment Page
 * Shows payment details for pending orders (crypto, manual payments)
 * Includes payment evidence submission for custom payment methods
 */

import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';

const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--tg-theme-bg-color, #ffffff);
  padding-bottom: 80px;
`;

const Header = styled.div`
  background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  padding: 16px;
  text-align: center;
  border-bottom: 1px solid var(--tg-theme-hint-color, #999999)20;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: var(--tg-theme-hint-color, #999999);
  margin: 8px 0 0 0;
`;

const Content = styled.div`
  padding: 16px;
`;

const PaymentCard = styled.div`
  background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
`;

const PaymentMethod = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--tg-theme-hint-color, #999999)20;
`;

const PaymentIcon = styled.span`
  font-size: 32px;
`;

const PaymentLabel = styled.div`
  flex: 1;
`;

const PaymentName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
`;

const PaymentCoin = styled.div`
  font-size: 14px;
  color: var(--tg-theme-hint-color, #999999);
`;

const AmountSection = styled.div`
  text-align: center;
  margin-bottom: 20px;
`;

const AmountLabel = styled.div`
  font-size: 14px;
  color: var(--tg-theme-hint-color, #999999);
  margin-bottom: 4px;
`;

const Amount = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: var(--tg-theme-text-color, #000000);
`;

const AddressSection = styled.div`
  background: var(--tg-theme-bg-color, #ffffff);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const AddressLabel = styled.div`
  font-size: 12px;
  color: var(--tg-theme-hint-color, #999999);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AddressValue = styled.div`
  font-size: 14px;
  font-family: monospace;
  color: var(--tg-theme-text-color, #000000);
  word-break: break-all;
  line-height: 1.5;
`;

const CopyButton = styled.button`
  width: 100%;
  padding: 12px;
  margin-top: 12px;
  background: var(--tg-theme-button-color, #3390ec);
  color: var(--tg-theme-button-text-color, #ffffff);
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:active {
    opacity: 0.8;
  }
`;

const QRCodeSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  background: #ffffff;
  border-radius: 12px;
  margin-bottom: 16px;
`;

const QRCodeImage = styled.img`
  width: 200px;
  height: 200px;
  border-radius: 8px;
  border: 4px solid var(--tg-theme-button-color, #3390ec);
`;

const QRCodeLabel = styled.div`
  font-size: 12px;
  color: var(--tg-theme-hint-color, #999999);
  margin-top: 12px;
  text-align: center;
`;

const Instructions = styled.div`
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const InstructionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #856404;
  margin-bottom: 8px;
`;

const InstructionText = styled.div`
  font-size: 13px;
  color: #856404;
  line-height: 1.5;
`;

const InstructionList = styled.ol`
  margin: 8px 0 0 0;
  padding-left: 20px;
`;

const InstructionItem = styled.li`
  margin-bottom: 4px;
`;

const OrderInfo = styled.div`
  background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
`;

const OrderRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid var(--tg-theme-hint-color, #999999)10;

  &:last-child {
    border-bottom: none;
  }
`;

const OrderLabel = styled.span`
  color: var(--tg-theme-hint-color, #999999);
  font-size: 14px;
`;

const OrderValue = styled.span`
  color: var(--tg-theme-text-color, #000000);
  font-size: 14px;
  font-weight: 500;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 14px;
  background: var(--tg-theme-button-color, #3390ec);
  color: var(--tg-theme-button-text-color, #ffffff);
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;

  &:active {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  padding: 14px;
  background: transparent;
  color: var(--tg-theme-link-color, #3390ec);
  border: 1px solid var(--tg-theme-link-color, #3390ec);
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;

  &:active {
    opacity: 0.8;
  }
`;

const RefreshButton = styled.button<{ $isRefreshing?: boolean }>`
  width: 100%;
  padding: 14px;
  background: #FF9800;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  opacity: ${props => props.$isRefreshing ? 0.7 : 1};

  &:active {
    opacity: 0.8;
  }

  &:disabled {
    cursor: not-allowed;
  }

  svg {
    animation: ${props => props.$isRefreshing ? 'spin 1s linear infinite' : 'none'};
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch (props.$status) {
      case 'completed': return '#d4edda';
      case 'pending': return '#fff3cd';
      case 'pending_verify': return '#cce5ff';
      case 'failed': return '#f8d7da';
      default: return '#e2e3e5';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'completed': return '#155724';
      case 'pending': return '#856404';
      case 'pending_verify': return '#004085';
      case 'failed': return '#721c24';
      default: return '#383d41';
    }
  }};
`;

// Payment Evidence Submission Styles
const EvidenceSection = styled.div`
  background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
`;

const EvidenceTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EvidenceDescription = styled.p`
  font-size: 14px;
  color: var(--tg-theme-hint-color, #999999);
  margin: 0 0 16px 0;
  line-height: 1.5;
`;

const EvidenceTypeSelector = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 16px;
`;

const EvidenceTypeButton = styled.button<{ $selected?: boolean }>`
  padding: 12px 8px;
  background: ${props => props.$selected
    ? 'var(--tg-theme-button-color, #3390ec)'
    : 'var(--tg-theme-bg-color, #ffffff)'};
  color: ${props => props.$selected
    ? 'var(--tg-theme-button-text-color, #ffffff)'
    : 'var(--tg-theme-text-color, #000000)'};
  border: 2px solid ${props => props.$selected
    ? 'var(--tg-theme-button-color, #3390ec)'
    : 'var(--tg-theme-hint-color, #999999)40'};
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--tg-theme-button-color, #3390ec);
  }

  span.icon {
    font-size: 24px;
  }
`;

const EvidenceInput = styled.input`
  width: 100%;
  padding: 14px;
  background: var(--tg-theme-bg-color, #ffffff);
  color: var(--tg-theme-text-color, #000000);
  border: 1px solid var(--tg-theme-hint-color, #999999)40;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 12px;
  box-sizing: border-box;

  &::placeholder {
    color: var(--tg-theme-hint-color, #999999);
  }

  &:focus {
    outline: none;
    border-color: var(--tg-theme-button-color, #3390ec);
  }
`;

const FileUploadArea = styled.label<{ $hasFile?: boolean; $isDragging?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: ${props => props.$hasFile
    ? '#d4edda'
    : props.$isDragging
      ? 'var(--tg-theme-button-color, #3390ec)10'
      : 'var(--tg-theme-bg-color, #ffffff)'};
  border: 2px dashed ${props => props.$hasFile
    ? '#28a745'
    : props.$isDragging
      ? 'var(--tg-theme-button-color, #3390ec)'
      : 'var(--tg-theme-hint-color, #999999)60'};
  border-radius: 10px;
  cursor: pointer;
  margin-bottom: 12px;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--tg-theme-button-color, #3390ec);
    background: var(--tg-theme-button-color, #3390ec)10;
  }
`;

const FileUploadIcon = styled.span`
  font-size: 40px;
  margin-bottom: 8px;
`;

const FileUploadText = styled.span`
  font-size: 14px;
  color: var(--tg-theme-text-color, #000000);
  font-weight: 500;
  text-align: center;
`;

const FileUploadHint = styled.span`
  font-size: 12px;
  color: var(--tg-theme-hint-color, #999999);
  margin-top: 4px;
`;

const SelectedFile = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: #d4edda;
  border-radius: 8px;
  margin-bottom: 12px;
`;

const FileName = styled.span`
  flex: 1;
  font-size: 14px;
  color: #155724;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  color: #721c24;
  font-size: 20px;
  cursor: pointer;
  padding: 4px;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const SubmitEvidenceButton = styled.button<{ $isSubmitting?: boolean }>`
  width: 100%;
  padding: 14px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  opacity: ${props => props.$isSubmitting ? 0.7 : 1};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const IvePaidButton = styled.button`
  width: 100%;
  padding: 14px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:active {
    opacity: 0.8;
  }
`;

const AwaitingConfirmationCard = styled.div`
  background: #cce5ff;
  border: 1px solid #b8daff;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  text-align: center;
`;

const AwaitingIcon = styled.div`
  font-size: 48px;
  margin-bottom: 12px;
`;

const AwaitingTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #004085;
  margin: 0 0 8px 0;
`;

const AwaitingText = styled.p`
  font-size: 14px;
  color: #004085;
  margin: 0;
  line-height: 1.5;
`;

type EvidenceType = 'screenshot' | 'transaction_id' | 'url' | null;

const Payment: React.FC = () => {
  const { botUsername } = useParams<{ botUsername: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Evidence submission states
  const [showEvidenceForm, setShowEvidenceForm] = useState(false);
  const [selectedEvidenceType, setSelectedEvidenceType] = useState<EvidenceType>(null);
  const [evidenceText, setEvidenceText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fetchedOrderId, setFetchedOrderId] = useState<string | null>(null);

  // Get payment details from URL params
  const orderIdFromUrl = searchParams.get('orderId') || '';
  const orderId = fetchedOrderId || orderIdFromUrl;
  const orderNumber = searchParams.get('order') || '';
  const fiatAmount = searchParams.get('fiatAmount') || searchParams.get('amount') || '0';
  const cryptoAmount = searchParams.get('cryptoAmount') || '';
  const paymentMethod = searchParams.get('method') || 'crypto';
  const coin = searchParams.get('coin') || '';
  const address = searchParams.get('address') || '';
  const network = searchParams.get('network') || '';
  const status = searchParams.get('status') || 'pending';
  // Telegram Stars params
  const starsAmount = searchParams.get('starsAmount') || '';
  // Gateway payment params (CryptoBot, etc.)
  const gateway = searchParams.get('gateway') || '';
  const gatewayPaymentId = searchParams.get('gatewayPaymentId') || '';
  const payUrl = searchParams.get('payUrl') || '';
  // Custom payment method params
  const methodName = searchParams.get('methodName') || '';
  const instruction = searchParams.get('instruction') || '';
  const customQrCode = searchParams.get('qrCode') || '';
  const customLink = searchParams.get('link') || '';
  // Requires manual confirmation (custom payment methods)
  const requiresEvidence = searchParams.get('requiresEvidence') === 'true' ||
    paymentMethod === 'custom' ||
    (paymentMethod !== 'wallet' && paymentMethod !== 'stripe' && paymentMethod !== 'paypal');

  const handleOpenPayUrl = () => {
    if (payUrl) {
      const tg = window.Telegram?.WebApp;
      // Use openTelegramLink for t.me URLs (CryptoBot, etc.) to stay inside Telegram
      if (payUrl.includes('t.me/') && tg?.openTelegramLink) {
        tg.openTelegramLink(payUrl);
      } else if (tg?.openLink) {
        tg.openLink(payUrl);
      } else {
        window.open(payUrl, '_blank');
      }
    }
  };

  const handleCopyAddress = async () => {
    if (address) {
      try {
        await navigator.clipboard.writeText(address);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);

        const tg = window.Telegram?.WebApp;
        tg?.showAlert?.(t('payment.addressCopied'));
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const handleViewOrder = () => {
    navigate(`/shop/${botUsername}/orders`);
  };

  const handleBackToShop = () => {
    navigate(`/shop/${botUsername}`);
  };

  const handleRefreshStatus = async () => {
    if (!orderNumber || isRefreshing) return;

    setIsRefreshing(true);
    const tg = window.Telegram?.WebApp;

    try {
      const token = localStorage.getItem('auth_token');
      const baseUrl = process.env.REACT_APP_API_URL || '';

      const response = await fetch(`${baseUrl}/api/orders/${botUsername}/by-number/${orderNumber}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success && data.data) {
        // Store the order ID for evidence submission
        if (data.data.id) {
          setFetchedOrderId(String(data.data.id));
        }

        const newStatus = data.data.status;
        const newPaymentStatus = data.data.payment_status;

        if (newPaymentStatus === 'paid' || newPaymentStatus === 'completed' ||
            newStatus === 'paid' || newStatus === 'completed') {
          // Payment completed - update URL and show success
          const newParams = new URLSearchParams(searchParams);
          newParams.set('status', 'completed');
          setSearchParams(newParams);
          tg?.showAlert?.(t('payment.statusUpdated'));
          tg?.HapticFeedback?.notificationOccurred('success');
        } else if (newStatus === 'pending_verify') {
          // Evidence submitted, awaiting confirmation
          const newParams = new URLSearchParams(searchParams);
          newParams.set('status', 'pending_verify');
          setSearchParams(newParams);
          tg?.showAlert?.(t('payment.awaitingConfirmation'));
          tg?.HapticFeedback?.notificationOccurred('warning');
        } else {
          // Still pending
          tg?.showAlert?.(t('payment.noStatusChange'));
          tg?.HapticFeedback?.notificationOccurred('warning');
        }
      }
    } catch (error) {
      console.error('Error refreshing status:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Fetch order ID on mount if not provided in URL
  React.useEffect(() => {
    const fetchOrderId = async () => {
      if (fetchedOrderId || !orderNumber) return;

      try {
        const token = localStorage.getItem('auth_token');
        const baseUrl = process.env.REACT_APP_API_URL || '';

        const response = await fetch(`${baseUrl}/api/orders/${botUsername}/by-number/${orderNumber}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();
        if (data.success && data.data?.id) {
          setFetchedOrderId(String(data.data.id));
        }
      } catch (error) {
        console.error('Error fetching order ID:', error);
      }
    };

    fetchOrderId();
  }, [botUsername, orderNumber, fetchedOrderId]);

  const handleIvePaid = () => {
    setShowEvidenceForm(true);
    const tg = window.Telegram?.WebApp;
    tg?.HapticFeedback?.impactOccurred('medium');
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        const tg = window.Telegram?.WebApp;
        tg?.showAlert?.(t('payment.invalidFileType'));
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        const tg = window.Telegram?.WebApp;
        tg?.showAlert?.(t('payment.fileTooLarge'));
        return;
      }
      setSelectedFile(file);
      setSelectedEvidenceType('screenshot');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        const tg = window.Telegram?.WebApp;
        tg?.showAlert?.(t('payment.invalidFileType'));
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        const tg = window.Telegram?.WebApp;
        tg?.showAlert?.(t('payment.fileTooLarge'));
        return;
      }
      setSelectedFile(file);
      setSelectedEvidenceType('screenshot');
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmitEvidence = async () => {
    if (isSubmitting) return;

    const tg = window.Telegram?.WebApp;

    // Validate evidence
    if (!selectedEvidenceType) {
      tg?.showAlert?.(t('payment.selectEvidenceType'));
      return;
    }

    if (selectedEvidenceType === 'screenshot' && !selectedFile) {
      tg?.showAlert?.(t('payment.uploadFile'));
      return;
    }

    if ((selectedEvidenceType === 'transaction_id' || selectedEvidenceType === 'url') && !evidenceText.trim()) {
      tg?.showAlert?.(t('payment.enterEvidence'));
      return;
    }

    // Validate URL format
    if (selectedEvidenceType === 'url' && !evidenceText.trim().startsWith('http')) {
      tg?.showAlert?.(t('payment.invalidUrl'));
      return;
    }

    // Ensure we have an order ID
    if (!orderId) {
      tg?.showAlert?.('Unable to submit evidence. Please refresh the page and try again.');
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('auth_token');
      const baseUrl = process.env.REACT_APP_API_URL || '';

      let evidenceFileId = null;

      // If file selected, upload it first to get file_id
      if (selectedFile && selectedEvidenceType === 'screenshot') {
        const formData = new FormData();
        formData.append('evidence', selectedFile);

        const uploadResponse = await fetch(`${baseUrl}/api/orders/${botUsername}/upload-evidence`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        const uploadData = await uploadResponse.json();

        if (!uploadData.success) {
          throw new Error(uploadData.error || 'Failed to upload file');
        }

        evidenceFileId = uploadData.data.fileId;
      }

      // Submit evidence
      const submitResponse = await fetch(`${baseUrl}/api/orders/${botUsername}/${orderId}/submit-evidence`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          evidenceType: selectedFile ? (selectedFile.type === 'application/pdf' ? 'pdf' : 'screenshot') : selectedEvidenceType,
          evidenceData: evidenceText.trim() || null,
          evidenceFileId: evidenceFileId
        })
      });

      const submitData = await submitResponse.json();

      if (!submitData.success) {
        throw new Error(submitData.error || 'Failed to submit evidence');
      }

      // Success - update status
      const newParams = new URLSearchParams(searchParams);
      newParams.set('status', 'pending_verify');
      setSearchParams(newParams);

      setShowEvidenceForm(false);
      tg?.showAlert?.(t('payment.evidenceSubmitted'));
      tg?.HapticFeedback?.notificationOccurred('success');

    } catch (error: any) {
      console.error('Error submitting evidence:', error);
      tg?.showAlert?.(error.message || t('payment.submitError'));
      tg?.HapticFeedback?.notificationOccurred('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPaymentIcon = () => {
    if (paymentMethod === 'crypto') return '💰';
    if (paymentMethod === 'wallet') return '👛';
    if (paymentMethod === 'telegram_stars') return '⭐';
    return '💳';
  };

  // Generate cryptocurrency payment URI with address and amount
  const getCryptoPaymentUri = (walletAddress: string, coinSymbol: string, payAmount: string) => {
    const coinLower = coinSymbol.toLowerCase();
    const amountNum = parseFloat(payAmount);

    // Map coin symbols to URI schemes
    const uriSchemes: { [key: string]: string } = {
      'btc': 'bitcoin',
      'bitcoin': 'bitcoin',
      'ltc': 'litecoin',
      'litecoin': 'litecoin',
      'eth': 'ethereum',
      'ethereum': 'ethereum',
      'usdt': 'tron',
      'usdt-trc20': 'tron',
      'trx': 'tron',
      'tron': 'tron',
      'doge': 'dogecoin',
      'dogecoin': 'dogecoin',
      'bch': 'bitcoincash',
      'xrp': 'ripple',
      'sol': 'solana',
      'bnb': 'bnb',
    };

    // Get the URI scheme for the coin, default to coin symbol
    const scheme = uriSchemes[coinLower] || coinLower.split('-')[0];

    // Build payment URI: scheme:address?amount=X
    return `${scheme}:${walletAddress}?amount=${amountNum}`;
  };

  // Generate QR code URL using QR Server API
  const getQRCodeUrl = (walletAddress: string, coinSymbol: string, payAmount: string) => {
    const paymentUri = getCryptoPaymentUri(walletAddress, coinSymbol, payAmount);
    const encodedData = encodeURIComponent(paymentUri);
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodedData}`;
  };

  const getPaymentTitle = () => {
    if (paymentMethod === 'crypto') return t('payment.cryptoPaymentTitle');
    if (paymentMethod === 'wallet') return t('payment.walletPaymentTitle');
    if (paymentMethod === 'telegram_stars') return t('payment.starsPaymentTitle');
    if (paymentMethod === 'custom') return methodName || t('payment.customPaymentTitle');
    return t('payment.paymentTitle');
  };

  // Generate QR code for custom payment (if they have a QR image URL or text)
  const getCustomQRCodeUrl = () => {
    if (customQrCode) {
      // If it's already a URL, return as-is
      if (customQrCode.startsWith('http')) return customQrCode;
      // Otherwise generate a QR code for the text
      return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(customQrCode)}`;
    }
    return null;
  };

  const getStatusLabel = (statusValue: string) => {
    switch (statusValue) {
      case 'completed':
        return t('payment.paid');
      case 'pending_verify':
        return t('payment.awaitingConfirmation');
      case 'pending':
      default:
        return t('payment.awaitingPayment');
    }
  };

  return (
    <PageContainer>
      <Header>
        <Title>
          {status === 'completed'
            ? t('payment.paymentSuccessful')
            : status === 'pending_verify'
              ? t('payment.evidenceSubmittedTitle')
              : t('payment.completePayment')}
        </Title>
        <Subtitle>{t('orders.orderNumber')}{orderNumber}</Subtitle>
      </Header>

      <Content>
        {status === 'completed' ? (
          <>
            <PaymentCard>
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>✅</div>
                <PaymentName>{t('payment.paymentCompleted')}</PaymentName>
                <PaymentCoin style={{ marginTop: '8px' }}>
                  {t('payment.orderConfirmed')}
                </PaymentCoin>
              </div>
            </PaymentCard>

            <ButtonGroup>
              <PrimaryButton onClick={handleViewOrder}>
                {t('payment.viewOrders')}
              </PrimaryButton>
              <SecondaryButton onClick={handleBackToShop}>
                {t('payment.continueShopping')}
              </SecondaryButton>
            </ButtonGroup>
          </>
        ) : status === 'pending_verify' ? (
          <>
            <AwaitingConfirmationCard>
              <AwaitingIcon>⏳</AwaitingIcon>
              <AwaitingTitle>{t('payment.awaitingConfirmation')}</AwaitingTitle>
              <AwaitingText>
                {t('payment.evidenceReceivedText')}
              </AwaitingText>
            </AwaitingConfirmationCard>

            <OrderInfo>
              <OrderRow>
                <OrderLabel>{t('payment.orderNumberLabel')}</OrderLabel>
                <OrderValue>#{orderNumber}</OrderValue>
              </OrderRow>
              <OrderRow>
                <OrderLabel>{t('payment.paymentMethodLabel')}</OrderLabel>
                <OrderValue>{coin || methodName || paymentMethod}</OrderValue>
              </OrderRow>
              <OrderRow>
                <OrderLabel>{t('payment.amountLabel')}</OrderLabel>
                <OrderValue>
                  {cryptoAmount ? `${cryptoAmount} ${coin}` : `$${parseFloat(fiatAmount).toFixed(2)}`}
                </OrderValue>
              </OrderRow>
              <OrderRow>
                <OrderLabel>{t('payment.statusLabel')}</OrderLabel>
                <OrderValue>
                  <StatusBadge $status="pending_verify">
                    {t('payment.awaitingConfirmation')}
                  </StatusBadge>
                </OrderValue>
              </OrderRow>
            </OrderInfo>

            <Instructions style={{ background: '#cce5ff', borderColor: '#b8daff' }}>
              <InstructionTitle style={{ color: '#004085' }}>
                ℹ️ {t('payment.whatHappensNext')}
              </InstructionTitle>
              <InstructionText style={{ color: '#004085' }}>
                <InstructionList>
                  <InstructionItem>{t('payment.confirmationStep1')}</InstructionItem>
                  <InstructionItem>{t('payment.confirmationStep2')}</InstructionItem>
                  <InstructionItem>{t('payment.confirmationStep3')}</InstructionItem>
                </InstructionList>
              </InstructionText>
            </Instructions>

            <ButtonGroup>
              <RefreshButton
                onClick={handleRefreshStatus}
                disabled={isRefreshing}
                $isRefreshing={isRefreshing}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                </svg>
                {isRefreshing ? t('payment.refreshing') : t('payment.checkStatus')}
              </RefreshButton>
              <SecondaryButton onClick={handleViewOrder}>
                {t('payment.viewOrders')}
              </SecondaryButton>
            </ButtonGroup>
          </>
        ) : showEvidenceForm ? (
          <>
            <EvidenceSection>
              <EvidenceTitle>
                📤 {t('payment.submitEvidence')}
              </EvidenceTitle>
              <EvidenceDescription>
                {t('payment.submitEvidenceDescription')}
              </EvidenceDescription>

              <EvidenceTypeSelector>
                <EvidenceTypeButton
                  $selected={selectedEvidenceType === 'screenshot' || selectedFile !== null}
                  onClick={() => {
                    setSelectedEvidenceType('screenshot');
                    setEvidenceText('');
                  }}
                >
                  <span className="icon">📸</span>
                  {t('payment.screenshot')}
                </EvidenceTypeButton>
                <EvidenceTypeButton
                  $selected={selectedEvidenceType === 'transaction_id' && !selectedFile}
                  onClick={() => {
                    setSelectedEvidenceType('transaction_id');
                    setSelectedFile(null);
                  }}
                >
                  <span className="icon">🔢</span>
                  {t('payment.transactionId')}
                </EvidenceTypeButton>
                <EvidenceTypeButton
                  $selected={selectedEvidenceType === 'url' && !selectedFile}
                  onClick={() => {
                    setSelectedEvidenceType('url');
                    setSelectedFile(null);
                  }}
                >
                  <span className="icon">🔗</span>
                  {t('payment.transactionUrl')}
                </EvidenceTypeButton>
              </EvidenceTypeSelector>

              {(selectedEvidenceType === 'screenshot' || !selectedEvidenceType) && (
                <>
                  {selectedFile ? (
                    <SelectedFile>
                      <span style={{ fontSize: '24px' }}>
                        {selectedFile.type === 'application/pdf' ? '📄' : '🖼️'}
                      </span>
                      <FileName>{selectedFile.name}</FileName>
                      <RemoveFileButton onClick={handleRemoveFile}>×</RemoveFileButton>
                    </SelectedFile>
                  ) : (
                    <FileUploadArea
                      $isDragging={isDragging}
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={handleDrop}
                    >
                      <HiddenFileInput
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={handleFileSelect}
                      />
                      <FileUploadIcon>📤</FileUploadIcon>
                      <FileUploadText>{t('payment.uploadScreenshot')}</FileUploadText>
                      <FileUploadHint>{t('payment.uploadHint')}</FileUploadHint>
                    </FileUploadArea>
                  )}
                </>
              )}

              {selectedEvidenceType === 'transaction_id' && !selectedFile && (
                <EvidenceInput
                  type="text"
                  placeholder={t('payment.transactionIdPlaceholder')}
                  value={evidenceText}
                  onChange={(e) => setEvidenceText(e.target.value)}
                />
              )}

              {selectedEvidenceType === 'url' && !selectedFile && (
                <EvidenceInput
                  type="url"
                  placeholder={t('payment.transactionUrlPlaceholder')}
                  value={evidenceText}
                  onChange={(e) => setEvidenceText(e.target.value)}
                />
              )}

              <SubmitEvidenceButton
                onClick={handleSubmitEvidence}
                disabled={isSubmitting || (!selectedFile && !evidenceText.trim())}
                $isSubmitting={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ animation: 'spin 1s linear infinite' }}>
                      <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                    </svg>
                    {t('payment.submitting')}
                  </>
                ) : (
                  <>
                    ✓ {t('payment.submitEvidenceBtn')}
                  </>
                )}
              </SubmitEvidenceButton>
            </EvidenceSection>

            <SecondaryButton onClick={() => setShowEvidenceForm(false)}>
              {t('common.back')}
            </SecondaryButton>
          </>
        ) : (
          <>
            <PaymentCard>
              <PaymentMethod>
                <PaymentIcon>{getPaymentIcon()}</PaymentIcon>
                <PaymentLabel>
                  <PaymentName>{getPaymentTitle()}</PaymentName>
                  {coin && <PaymentCoin>{coin}</PaymentCoin>}
                </PaymentLabel>
                <StatusBadge $status={status}>
                  {getStatusLabel(status)}
                </StatusBadge>
              </PaymentMethod>

              <AmountSection>
                {cryptoAmount ? (
                  <>
                    <AmountLabel>{t('payment.amountToPay')}</AmountLabel>
                    <Amount>{cryptoAmount} {coin}</Amount>
                    <AmountLabel style={{ marginTop: '8px', fontSize: '14px' }}>
                      {t('payment.usdEquivalent', { amount: `$${parseFloat(fiatAmount).toFixed(2)}` })}
                    </AmountLabel>
                  </>
                ) : (
                  <>
                    <AmountLabel>{t('payment.amountToPay')}</AmountLabel>
                    <Amount>${parseFloat(fiatAmount).toFixed(2)}</Amount>
                  </>
                )}
              </AmountSection>

              {payUrl && !address && (
                <>
                  <div style={{
                    textAlign: 'center',
                    padding: '20px',
                    background: 'var(--tg-theme-bg-color, #ffffff)',
                    borderRadius: '12px',
                    marginBottom: '16px'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>
                      {gateway === 'cryptobot' ? '🤖' : '🔗'}
                    </div>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: 'var(--tg-theme-text-color, #000000)',
                      marginBottom: '8px'
                    }}>
                      {gateway === 'cryptobot'
                        ? 'Pay with CryptoBot'
                        : `Pay via ${gateway || 'Payment Gateway'}`}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: 'var(--tg-theme-hint-color, #999999)',
                      marginBottom: '16px'
                    }}>
                      {gateway === 'cryptobot'
                        ? 'Click below to open CryptoBot and complete your payment. You can choose any supported cryptocurrency.'
                        : 'Click below to open the payment page and complete your payment.'}
                    </div>
                    <PrimaryButton onClick={handleOpenPayUrl} style={{ marginBottom: '8px' }}>
                      {gateway === 'cryptobot' ? '🤖 Open CryptoBot' : '🔗 Open Payment Page'}
                    </PrimaryButton>
                  </div>

                  {gateway === 'cryptobot' && (
                    <Instructions>
                      <InstructionTitle>ℹ️ How to pay with CryptoBot</InstructionTitle>
                      <InstructionText>
                        <InstructionList>
                          <InstructionItem>Tap "Open CryptoBot" to open the payment page</InstructionItem>
                          <InstructionItem>Select your preferred cryptocurrency</InstructionItem>
                          <InstructionItem>Complete the payment in CryptoBot</InstructionItem>
                          <InstructionItem>Come back here and tap "Check Status" to confirm</InstructionItem>
                        </InstructionList>
                      </InstructionText>
                    </Instructions>
                  )}
                </>
              )}

              {address && (
                <>
                  <QRCodeSection>
                    <QRCodeImage
                      src={getQRCodeUrl(address, coin, cryptoAmount || fiatAmount)}
                      alt={`QR Code for ${coin} payment`}
                    />
                    <QRCodeLabel>
                      {t('payment.scanToPay', { amount: cryptoAmount || fiatAmount, coin })}
                      {network && <span style={{ display: 'block', marginTop: '4px' }}>{t('payment.networkLabel', { network })}</span>}
                    </QRCodeLabel>
                  </QRCodeSection>

                  <AddressSection>
                    <AddressLabel>{t('payment.sendToAddress', { amount: cryptoAmount, coin })}</AddressLabel>
                    <AddressValue>{address}</AddressValue>
                    <CopyButton onClick={handleCopyAddress}>
                      {copied ? `✓ ${t('payment.copied')}` : `📋 ${t('payment.copyAddress')}`}
                    </CopyButton>
                  </AddressSection>
                </>
              )}
            </PaymentCard>

            {paymentMethod === 'crypto' && (
              <Instructions>
                <InstructionTitle>⚠️ {t('payment.importantInstructions')}</InstructionTitle>
                <InstructionText>
                  <InstructionList>
                    <InstructionItem><strong>{t('payment.cryptoInstruction1', { amount: cryptoAmount, coin })}</strong></InstructionItem>
                    <InstructionItem>{network ? t('payment.cryptoInstruction2Network', { coin, network }) : t('payment.cryptoInstruction2', { coin })}</InstructionItem>
                    <InstructionItem>{t('payment.cryptoInstruction3')}</InstructionItem>
                    <InstructionItem>{t('payment.cryptoInstruction4')}</InstructionItem>
                  </InstructionList>
                </InstructionText>
              </Instructions>
            )}

            {paymentMethod === 'telegram_stars' && (
              <Instructions>
                <InstructionTitle>⭐ {t('payment.starsPaymentTitle')}</InstructionTitle>
                <InstructionText>
                  {starsAmount && (
                    <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px' }}>
                      {t('payment.starsAmount', { amount: starsAmount })} ⭐
                    </div>
                  )}
                  {t('payment.starsInvoiceSent')}
                  <InstructionList>
                    <InstructionItem>{t('payment.starsInstruction1')}</InstructionItem>
                    <InstructionItem>{t('payment.starsInstruction2')}</InstructionItem>
                    <InstructionItem>{t('payment.starsInstruction3')}</InstructionItem>
                  </InstructionList>
                </InstructionText>
              </Instructions>
            )}

            {paymentMethod === 'custom' && (
              <>
                {getCustomQRCodeUrl() && (
                  <QRCodeSection>
                    <QRCodeImage
                      src={getCustomQRCodeUrl()!}
                      alt={`QR Code for ${methodName} payment`}
                    />
                    <QRCodeLabel>{t('payment.scanToPayCustom')}</QRCodeLabel>
                  </QRCodeSection>
                )}
                {instruction && (
                  <Instructions>
                    <InstructionTitle>📋 {t('payment.customPaymentInstructions')}</InstructionTitle>
                    <InstructionText style={{ whiteSpace: 'pre-wrap' }}>
                      {instruction}
                    </InstructionText>
                  </Instructions>
                )}
                {customLink && (
                  <ButtonGroup style={{ marginTop: '16px' }}>
                    <PrimaryButton
                      as="a"
                      href={customLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none', textAlign: 'center' }}
                    >
                      {t('payment.openPaymentLink')}
                    </PrimaryButton>
                  </ButtonGroup>
                )}
              </>
            )}

            <OrderInfo>
              <OrderRow>
                <OrderLabel>{t('payment.orderNumberLabel')}</OrderLabel>
                <OrderValue>#{orderNumber}</OrderValue>
              </OrderRow>
              <OrderRow>
                <OrderLabel>{t('payment.paymentMethodLabel')}</OrderLabel>
                <OrderValue>{coin || methodName || paymentMethod}</OrderValue>
              </OrderRow>
              <OrderRow>
                <OrderLabel>{t('payment.statusLabel')}</OrderLabel>
                <OrderValue>
                  <StatusBadge $status={status}>
                    {getStatusLabel(status)}
                  </StatusBadge>
                </OrderValue>
              </OrderRow>
            </OrderInfo>

            <ButtonGroup>
              {requiresEvidence && (
                <IvePaidButton onClick={handleIvePaid}>
                  ✅ {t('payment.ivePaid')}
                </IvePaidButton>
              )}
              <RefreshButton
                onClick={handleRefreshStatus}
                disabled={isRefreshing}
                $isRefreshing={isRefreshing}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                </svg>
                {isRefreshing ? t('payment.refreshing') : t('payment.refreshStatus')}
              </RefreshButton>
              <SecondaryButton onClick={handleViewOrder}>
                {t('payment.viewOrders')}
              </SecondaryButton>
            </ButtonGroup>
          </>
        )}
      </Content>
    </PageContainer>
  );
};

export default Payment;
